import React, { useState, useEffect } from 'react';
//import { Link } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { userRequest } from '../../../utils/requestMethods';
import { Header, CustomPagination, SkeletonTable, AlertNotification, AddTestModal, EditTestModal, ConfirmationModal } from '../../../components';
import { useStateContext } from '../../../contexts/ContextProvider';
import { toast } from 'react-toastify';
import { MdSearch, MdAdd, MdQuestionAnswer, MdSettings } from 'react-icons/md';
import { FiMoreHorizontal, FiCheck, FiX, FiPlay, FiPause, FiFileText, FiTrash2, FiTrendingUp } from 'react-icons/fi';
import { TableDropdown } from '../../../components';
import { generateSchoolSessions, displaySchoolTerm } from '../../../utils/schoolYearUtils';
import { getArmLabel, pluralize } from '../../../utils/classGroupHelpers';

const customStyles = {
  headRow: {
    style: {
      backgroundColor: 'rgb(248, 250, 252)',
      fontWeight: "bold",
    },
  },
};

const TeacherTests = ({ history }) => {
  const { user, school } = useStateContext();
  const schoolTerm = school?.settings?.current_term;
  const schoolSession = school?.settings?.current_session;
  
  const [tests, setTests] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [filterText, setFilterText] = useState('');
  // "Create test for all arms" elsewhere (e.g. My Subjects) lands here with the class level
  // and shared subject in location state, so the create modal opens pre-filled.
  const [createPrefill, setCreatePrefill] = useState(() => history?.location?.state?.createTest || null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(() => Boolean(history?.location?.state?.createTest));
  const armsLower = pluralize(getArmLabel(school), 2).toLowerCase();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [dropdownIndex, setDropdownIndex] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [testToDelete, setTestToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Term & Session filter state with localStorage persistence
  const [termFilter, setTermFilter] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('teacherTestsSelectedTerm') || 'null');
      return saved?.id || schoolTerm || '1';
    } catch (error) {
      console.error('Error loading termFilter from storage:', error);
    }
    return schoolTerm || '1';
  });
  const [sessionFilter, setSessionFilter] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('teacherTestsSelectedSession') || 'null');
      return saved?.id || schoolSession || '2024/2025';
    } catch (error) {
      console.error('Error loading sessionFilter from storage:', error);
    }
    return schoolSession || '2024/2025';
  });

  // Generate school sessions for session filter
  const schoolSessions = generateSchoolSessions(2020, 10);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocuments, setTotalDocuments] = useState(0);

  const toggleCreateModal = () => {
    if (isCreateModalOpen) setCreatePrefill(null);
    setIsCreateModalOpen(!isCreateModalOpen);
  };

  // Clear the one-off prefill from history so a refresh doesn't reopen the modal.
  useEffect(() => {
    if (history?.location?.state?.createTest) {
      history.replace({ ...history.location, state: { ...history.location.state, createTest: undefined } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEditModal = (test) => {
    setSelectedTest(test);
    setIsEditModalOpen(true);
    setDropdownIndex(null);
  };

  const closeEditModal = () => {
    setSelectedTest(null);
    setIsEditModalOpen(false);
  };

  const handleDropdownClick = (rowIndex, event) => {
    const rect = event.target.getBoundingClientRect();
    setDropdownIndex(dropdownIndex === rowIndex ? null : rowIndex);
    setDropdownPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
  };

  const handleCloseDropdown = () => setDropdownIndex(null);

  const confirmDeleteTest = (test) => {
    setTestToDelete(test);
    setShowDeleteModal(true);
    setDropdownIndex(null);
  };

  const cancelDeleteTest = () => {
    setShowDeleteModal(false);
    setTestToDelete(null);
  };

  const handleDeleteTest = async () => {
    if (!testToDelete) return;

    try {
      setDeleting(true);
      await userRequest.delete(`/test/${testToDelete._id}`);
      toast.success('Test deleted successfully');
      
      // Close modal and reset state
      setShowDeleteModal(false);
      setTestToDelete(null);
      
      // Refresh the tests list - maintain current page
      fetchTests(currentPage, perPage, filterText, termFilter, sessionFilter);
    } catch (error) {
      console.error('Error deleting test:', error);
      toast.error('Failed to delete test');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (test) => {
    if (actionLoading) return;
    
    setActionLoading(true);
    try {
      const newActiveStatus = !test.is_active;
      // A shared test has no single class — test.class here is the class GROUP shown for
      // display purposes (enriched server-side), not a real class id, and must never be sent
      // back as `class` on an edit (that would silently overwrite class_group_id scoping).
      const isSharedTest = Boolean(test.class_group_id);
      const requestBody = {
        title: test.title,
        description: test.description,
        duration: test.duration,
        total_score: test.total_score,
        school: test.school._id,
        ...(isSharedTest ? {} : { class: test.class._id, subject: test.subject?._id }),
        is_active: newActiveStatus,
        term: test.term,
        session: test.session,
        type: test.type,
        ca: test.ca,
        status: test.status
      };

      await userRequest.put(`/test/${test._id}`, requestBody);
      
      toast.success(`Test ${newActiveStatus ? 'activated' : 'deactivated'} successfully!`);
      fetchTests(currentPage, perPage, filterText, termFilter, sessionFilter); // Refresh the list - maintain current page
      setDropdownIndex(null);
    } catch (error) {
      console.error('Error updating test status:', error);
      toast.error('Failed to update test status');
    } finally {
      setActionLoading(false);
    }
  };

  const fetchTests = async (
    page = currentPage,
    limit = perPage,
    search = filterText,
    term = termFilter,
    session = sessionFilter
  ) => {
    if (!school?._id) return;
    
    setLoading(true);
    try {
      // Build query string with search, term and session parameters
      let queryString = `/test?school_id=${school?._id}&page=${page}&limit=${limit}&sort_by=date&order=desc`;
      if (search && search.trim()) {
        queryString += `&search=${encodeURIComponent(search.trim())}`;
      }
      if (term && term !== 'all') {
        queryString += `&term=${term}`;
      }
      if (session && session !== 'all') {
        queryString += `&session=${encodeURIComponent(session)}`;
      }
      
      const { data } = await userRequest.get(queryString);
      console.log(data);
      setTests(data.tests || []);
      setTotalPages(data.total_pages || 1);
      setTotalDocuments(data.total_documents || 0);
      setCurrentPage(data.page || 1);
    } catch (error) {
      setError("Error fetching tests:", error);
      console.error("Error fetching tests:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubjects = async () => {
    setLoading(true);
    try {
      const { data } = await userRequest.get(`/teachers/teacher-assigned-subjects/${school?._id}/${user?._id}?limit=100`);
      setSubjects(data.subjects || []);
    } catch (error) {
      console.error("Error fetching subjects:", error);
      setSubjects([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (school?._id && user?._id) {
      fetchTests(1, perPage, filterText, termFilter, sessionFilter); // Always start from page 1 on initial load
      fetchSubjects();
    }
  }, [school?._id, user?._id]);
  
  // Handle search with debouncing
  useEffect(() => {
    if (!school?._id || !user?._id) return;
    
    const searchTimer = setTimeout(() => {
      // Reset to page 1 when search changes
      setCurrentPage(1);
      fetchTests(1, perPage, filterText, termFilter, sessionFilter);
    }, 500); // 500ms debounce
    
    return () => clearTimeout(searchTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterText]);

  // Handle term and session filter changes
  useEffect(() => {
    if (!school?._id || !user?._id) return;
    
    // Reset to page 1 when term or session changes
    setCurrentPage(1);
    fetchTests(1, perPage, filterText, termFilter, sessionFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termFilter, sessionFilter]);
  
  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Draft', icon: FiFileText },
      published: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Published', icon: FiCheck },
      completed: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Completed', icon: FiCheck },
      cancelled: { bg: 'bg-rose-100', text: 'text-rose-700', label: 'Cancelled', icon: FiX }
    };

    const config = statusConfig[status] || statusConfig.draft;
    const IconComponent = config.icon;
    
    return (
      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        <IconComponent className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const getActiveBadge = (isActive) => {
    if (isActive) {
      return (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">
          <FiPlay className="w-3 h-3 mr-1" />
          Active
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
        <FiPause className="w-3 h-3 mr-1" />
        Inactive
      </span>
    );
  };

  const getTypeBadge = (type, ca) => {
    if (type === 'ca') {
      return (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-700">
          CA {ca}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
        {type?.toUpperCase() || 'EXAM'}
      </span>
    );
  };

  const columns = [
    {
      name: "Test Details",
      selector: row => row.title,
      cell: row => (
        <div className="py-2">
          <div className="font-semibold text-slate-800 mb-1">{row.title}</div>
          {/*<div className="text-xs text-slate-500 truncate max-w-xs">{row.description}</div>*/}
          <div className="flex items-center space-x-1.5 mt-2">
            {getStatusBadge(row.status)}
            {getActiveBadge(row.is_active)}
            {getTypeBadge(row.type, row.ca)}
          </div>
        </div>
      ),
      sortable: true,
      width: "280px",
      minWidth: "120px"
    },
    {
      name: "Subject & Class",
      selector: row => row.subject?.subject_name,
      cell: row => (
        <div className="py-2">
          <div className="font-semibold text-slate-800">{row.subject?.subject_name}</div>
          {/*<div className="text-xs text-slate-500">{row.subject?.subject_code}</div>*/}
          <div className="text-sm text-slate-600 mt-1 flex items-center gap-1.5 flex-wrap">
            <span>{row.class?.class_name}</span>
            {row.class_group_id && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-indigo-50 text-indigo-700">
                All {armsLower}
              </span>
            )}
          </div>
        </div>
      ),
      sortable: true,
      //width: "180px"
    },
    {
      name: "Duration & Score",
      selector: row => row.duration,
      cell: row => (
        <div className="py-2">
          <div className="font-semibold text-slate-800">{row.duration} mins</div>
          <div className="text-sm text-slate-600">{row.total_score} points</div>
        </div>
      ),
      sortable: true,
      //width: "100px"
    },
    {
      name: "Created",
      selector: row => row.createdAt,
      cell: row => (
        <div className="py-2">
          <div className="text-sm font-medium text-slate-800">
            {new Date(row.createdAt).toLocaleDateString()}
          </div>
          <div className="text-xs text-slate-500">
            {new Date(row.createdAt).toLocaleTimeString()}
          </div>
        </div>
      ),
      sortable: true,
      width: "140px",
      minWidth: "120px"
    },
    {
      name: "Options",
      cell: (row, rowIndex) => (
        <div className="relative py-2">
          <button
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all duration-200"
            onClick={(e) => handleDropdownClick(rowIndex, e)}
          >
            <FiMoreHorizontal className="text-xl" />
          </button>
          <TableDropdown
            isVisible={dropdownIndex === rowIndex}
            position={dropdownPosition}
            options={[
              {
                label: "Edit Test Questions",
                icon: <MdQuestionAnswer className='text-purple-500 text-lg' />,
                onClick: () => {
                  setDropdownIndex(null);
                  history.push('/t/tests/edit-questions', { test: row });
                },
              },
              {
                label: "Edit Test Settings",
                icon: <MdSettings className='text-orange-500 text-lg' />,
                onClick: () => openEditModal(row),
              },
              {
                label: "View Results",
                icon: <FiTrendingUp className='text-blue-500 text-lg' />,
                onClick: () => {
                  setDropdownIndex(null);
                  history.push({
                    pathname: `/t/tests/results/${row._id}/${school._id}`,
                    state: {
                      testDetails: {
                        _id: row._id,
                        title: row.title,
                        description: row.description,
                        subject: row.subject,
                        class: row.class,
                        type: row.type,
                        ca: row.ca,
                        total_score: row.total_score,
                        duration: row.duration,
                        status: row.status,
                        is_active: row.is_active,
                        createdAt: row.createdAt
                      }
                    }
                  });
                },
              },
              /*{
                label: row.is_active ? "Deactivate Test" : "Activate Test",
                icon: row.is_active ? 
                  <FiPause className='text-red-500 text-lg' /> : 
                  <FiPlay className='text-green-500 text-lg' />,
                onClick: () => handleToggleActive(row),
                disabled: actionLoading,
              },*/
              {
                label: "Delete Test",
                icon: <FiTrash2 className='text-red-500 text-lg' />,
                onClick: () => confirmDeleteTest(row),
                danger: true,
              }
            ]}
            onClose={handleCloseDropdown}
          />
        </div>
      ),
      width: "120px",
      minWidth: "100px",
      ignoreRowClick: true,
      button: true,
    },
  ];

  // Search is now handled server-side, so use tests directly
  const filteredData = tests;

  return (
    <>
    <div className="px-3 md:px-10 pt-10 md:pt-5 mt-10">
      <div className="flex flex-wrap justify-between items-center mb-4">
        <Header category="Online Test" title="Create Tests" />
      </div>
    </div>

    <div className="m-2 md:mx-10 p-4 md:p-10 bg-white rounded-3xl dark:bg-secondary-dark-bg min-h-screen overflow-x-auto max-w-full" style={{ maxWidth: "1090px" }}>
      <div className="flex flex-wrap justify-between items-center mb-4">
        <h1 className="text-lg font-semibold w-full md:w-auto mb-2 md:mb-0" style={{ maxWidth: "20em" }}>
          All Tests
        </h1>
        <div className="flex flex-wrap justify-center md:justify-end w-full md:w-auto gap-2">
          {/* Search Input */}
          <form className="w-full md:w-auto">
            <div className="relative">
              <div className="absolute inset-y-0 start-0 flex items-center pl-4 pointer-events-none">
                <MdSearch />
              </div>
              <input type="text" value={filterText} onChange={(e) => setFilterText(e.target.value)} className="w-full sm:w-auto bg-gray-50 border border-gray-300 text-gray-900 text-sm sm:text-md rounded-full focus:ring-blue-500 focus:border-blue-500 block pl-8 p-2.5" placeholder="Search"/>
            </div>
          </form>
          
          {/* Term Filter */}
          <select
            value={termFilter}
            onChange={(e) => {
              const newTerm = e.target.value;
              setTermFilter(newTerm);
              localStorage.setItem(
                'teacherTestsSelectedTerm',
                JSON.stringify({
                  id: newTerm,
                  name: newTerm === 'all' ? 'All Terms' : `Term ${newTerm}`,
                })
              );
            }}
            className="px-3 py-2.5 border border-gray-300 rounded-full bg-white text-sm text-gray-900 focus:ring-blue-500 focus:border-blue-500 min-w-[140px]"
          >
            <option value="all">All Terms</option>
            <option value="1">{displaySchoolTerm(1) || '1st Term'}</option>
            <option value="2">{displaySchoolTerm(2) || '2nd Term'}</option>
            <option value="3">{displaySchoolTerm(3) || '3rd Term'}</option>
          </select>

          {/* Session Filter */}
          <select
            value={sessionFilter}
            onChange={(e) => {
              const newSession = e.target.value;
              setSessionFilter(newSession);
              const sessionLabel =
                newSession === 'all'
                  ? 'All Sessions'
                  : schoolSessions.find((s) => s.value === newSession)?.label || newSession;
              localStorage.setItem(
                'teacherTestsSelectedSession',
                JSON.stringify({
                  id: newSession,
                  name: sessionLabel,
                })
              );
            }}
            className="px-3 py-2.5 border border-gray-300 rounded-full bg-white text-sm text-gray-900 focus:ring-blue-500 focus:border-blue-500 min-w-[150px]"
          >
            <option value="all">All Sessions</option>
            {schoolSessions.map((session) => (
              <option key={session.value} value={session.value}>
                {session.label}
              </option>
            ))}
          </select>

          <div>
            <button type="button" onClick={toggleCreateModal} className="w-full inline-flex items-center justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-full text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2">
              <MdAdd className="mr-2" size={18} />
              Create New Test
            </button>
          </div>
        </div>
      </div>
      <div className="border rounded-3xl">
        <DataTable
          data={filteredData}
          columns={columns}
          getRowId={(row) => row._id}
          pagination
          paginationServer
          paginationTotalRows={totalDocuments}
          paginationDefaultPage={currentPage}
          paginationPerPage={perPage}
          paginationComponent={CustomPagination}
          paginationRowsPerPageOptions={[10, 20, 30, 50]}
          onChangePage={(page) => {
            setCurrentPage(page);
            fetchTests(page, perPage, filterText, termFilter, sessionFilter);
          }}
          onChangeRowsPerPage={(newPerPage, page) => {
            setPerPage(newPerPage);
            setCurrentPage(page);
            fetchTests(page, newPerPage, filterText, termFilter, sessionFilter);
          }}
          customStyles={customStyles}
          progressPending={loading}
          progressComponent={<SkeletonTable rows={5} columns={5} />}
          highlightOnHover
          striped
          noDataComponent={
            <div className="text-center py-16">
              <div className="mx-auto h-24 w-24 text-slate-300 mb-6 flex items-center justify-center">
                <FiFileText className="w-16 h-16" />
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-2">No tests found</h3>
              <p className="text-slate-500 text-sm">Create your first test to get started</p>
            </div>
          }
        />
      </div>
    </div>

    {/* Create Test Modal */}
    {isCreateModalOpen && (
      <AddTestModal
        closeModal={toggleCreateModal} 
        onTestAdded={() => {
          // Reset to page 1 after creating a new test
          setCurrentPage(1);
          fetchTests(1, perPage, filterText, termFilter, sessionFilter);
        }}
        subjects={subjects}
        teacherId={user._id}
        school={school}
        prefill={createPrefill}
      />
    )}

    {/* Edit Test Modal */}
    {isEditModalOpen && selectedTest && (
      <EditTestModal
        closeModal={closeEditModal} 
        onTestUpdated={() => {
          // Refresh current page after updating
          fetchTests(currentPage, perPage, filterText, termFilter, sessionFilter);
        }}
        subjects={subjects}
        test={selectedTest}
        school={school}
      />
    )}

    {/* Delete Confirmation Modal */}
    <ConfirmationModal
      isVisible={showDeleteModal}
      title="Delete Test"
      description={`Are you sure you want to delete the test "${testToDelete?.title}"? This action cannot be undone and will permanently remove all associated questions and data.`}
      onConfirm={handleDeleteTest}
      onCancel={cancelDeleteTest}
      deleting={deleting}
    />

    {error && (
      <AlertNotification type="error" message={error} onClose={() => setError("")} />
    )}
    </>
  );
};

export default TeacherTests;
